import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button } from "src/components/base/button";
import React, { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ApiInternal } from "src/api/api_internal";
import { DeviceFromIpResponse, ExtraDnsRecordType, FullExtraDnsDomain } from "src/api/generated/frontend_internal_v1";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { BadgeButton } from "src/components/base/badges/base";
import { LinkIcon } from "@heroicons/react/20/solid";

/**
 * The properties for {@link AddExtraRecordDialog}
 */
export type AddExtraRecordDialogProps = {
    /** The dns to add the record to  */
    dns: FullExtraDnsDomain;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for adding the domain */
    onSubmit: () => void;
};

/**
 * A dialog for adding a new extra dns domain
 */
export default function AddExtraRecordDialog(props: AddExtraRecordDialogProps) {
    const [t] = useTranslation("dialog-add-extra-record");
    const [device, setDevice] = React.useState<DeviceFromIpResponse | undefined>(undefined);

    const form = useForm({
        defaultValues: {
            record_type: ExtraDnsRecordType.A,
            ip: "",
        },
        validators: {
            onSubmitAsync: async (values) => {
                const res = await ApiInternal.dns.addSingleExtraDnsRecord(props.dns.uuid, {
                    record_type: values.value.record_type,
                    to_ip: values.value.ip,
                });
                if (res.result === "Err") {
                    return {
                        fields: {
                            ip: res.error.ip_invalid
                                ? t("error.invalid-ip")
                                : res.error.no_internal_device
                                  ? t("error.no-internal-device")
                                  : res.error.ip_address_exists
                                    ? t("error.ip-address-already-exists")
                                    : undefined,
                        },
                    };
                }
                props.onSubmit();
            },
        },
    });

    const formIp = useStore(form.store, (store) => store.values.ip);
    useEffect(() => {
        const timeout = setTimeout(async () => {
            setDevice(await ApiInternal.devices.getFromIp(formIp));
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    }, [formIp]);

    return (
        <Dialog onClose={props.onClose} open={true}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.update-extra-domain")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"record_type"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.record-type")}</Label>
                                        <Listbox
                                            onChange={fieldApi.handleChange}
                                            name="record-type"
                                            value={fieldApi.state.value}
                                        >
                                            <ListboxOption value={ExtraDnsRecordType.A}>
                                                <ListboxLabel>{ExtraDnsRecordType.A}</ListboxLabel>
                                            </ListboxOption>
                                            <ListboxOption value={ExtraDnsRecordType.AAAA}>
                                                <ListboxLabel>{ExtraDnsRecordType.AAAA}</ListboxLabel>
                                            </ListboxOption>
                                        </Listbox>
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"ip"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.ip")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            max={255}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />

                                        {device && (
                                            <BadgeButton
                                                className={"mt-4"}
                                                color={"zinc"}
                                                target={"_blank"}
                                                href={"/a/devices/$deviceId/general"}
                                                params={{ deviceId: device.uuid }}
                                            >
                                                <LinkIcon className={"w-4"} />
                                                <span> {device.name}</span>
                                            </BadgeButton>
                                        )}

                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <Button type={"submit"} color={"blue"}>
                        {t("button.add")}
                    </Button>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
